import { useParams, useSearchParams } from 'react-router-dom';
import { systemClock } from '@sakhtar/calendar';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { baseOrganizationPayloadOf, setupStepView, type SetupView } from '../../lib/types/setup.js';
import { formatRialFa, type AmountDisplayUnit } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type { TrialBalanceResponse } from '../../lib/types/finance.js';
import { isReady, PrintLoadState, PrintPageShell, TenantIdentityBlock } from './PrintChrome.js';

/** Same "بد"/"بس" text rule as TrialBalancePage.tsx — debit/credit nature is
 * spoken, never shown by color alone (color doesn't survive a black/white
 * print anyway). */
function balanceLabel(
  row: { debitBalanceRial: string; creditBalanceRial: string },
  unit: AmountDisplayUnit,
): string {
  const debit = BigInt(row.debitBalanceRial);
  const credit = BigInt(row.creditBalanceRial);
  if (debit > 0n) return `${formatRialFa(row.debitBalanceRial, unit)} بد`;
  if (credit > 0n) return `${formatRialFa(row.creditBalanceRial, unit)} بس`;
  return '—';
}

/**
 * تراز آزمایشی (trial balance) print view — the first accounting report to
 * get one. Reuses the same `PrintPageShell`/`TenantIdentityBlock` chrome as
 * the CRM quote/order/customer-report print views (`routes/print/
 * PrintChrome.tsx`'s own doc comment already names "پیش‌فاکتور/فاکتور/گزارش"
 * as its three intended callers; this is the first "گزارش" one). Data comes
 * from the same real, posted-GL-only endpoint TrialBalancePage.tsx already
 * uses (`GET .../finance/reports/trial-balance?asOfDate=...`) — `asOfDate`
 * is read from the query string so the print link can carry the date the
 * user picked on that page without duplicating the date picker here.
 *
 * UI_REFERENCE_SOURCE: Sakhtar Print Documents.dc.html, page 1 layout
 *   (shared document chrome) plus TrialBalancePage.tsx's own already-real
 *   table/footer columns — no new fields invented for print. status:
 *   PARTIAL_REAL, same account-level/fiscal-period gaps TrialBalancePage.tsx
 *   already discloses (no `level` or `fiscalPeriodCode` filter wired here
 *   either).
 */
export function TrialBalancePrintPage() {
  const { unit, label, ready } = useAmountDisplay();
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const asOfDate = searchParams.get('asOfDate') ?? '';
  const base = slug ? tenantBasePath(slug) : '';
  const report = useApiGet<TrialBalanceResponse>(
    slug && asOfDate ? `${base}/api/v1/finance/reports/trial-balance?asOfDate=${asOfDate}` : null,
    [slug, asOfDate],
  );
  const setup = useApiGet<SetupView>(slug ? `${base}/api/v1/setup` : null, [slug]);

  if (!slug) return null;
  if (!ready) {
    return (
      <PrintPageShell
        backHref={`${base}/finance/reports/trial-balance`}
        backLabel="بازگشت به تراز آزمایشی"
      >
        <PrintLoadState />
      </PrintPageShell>
    );
  }

  const backHref = `${base}/finance/reports/trial-balance`;

  if (!asOfDate || !isReady(report)) {
    return (
      <PrintPageShell backHref={backHref} backLabel="بازگشت به تراز آزمایشی">
        <PrintLoadState error={report.status === 'error' ? report.error : undefined} />
      </PrintPageShell>
    );
  }

  const baseOrg = isReady(setup)
    ? baseOrganizationPayloadOf(setupStepView(setup.data, 'base-organization'))
    : null;
  const today = systemClock.today();
  const rows = report.data.rows;

  return (
    <PrintPageShell backHref={backHref} backLabel="بازگشت به تراز آزمایشی">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontSize: 12.5 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
          <TenantIdentityBlock
            legalIdentity={baseOrg?.legalIdentity ?? null}
            primaryLocation={baseOrg?.primaryLocation ?? null}
            personType={baseOrg?.personType ?? null}
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              alignItems: 'flex-end',
              textAlign: 'left',
            }}
          >
            <span style={{ fontSize: 18, fontWeight: 700 }}>تراز آزمایشی</span>
            <span>تا تاریخ {displayJalaliDate(report.data.asOfDate)}</span>
            <span>تاریخ تهیه {displayJalaliDate(today)}</span>
          </div>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #333' }}>
              <th style={{ textAlign: 'right', padding: '6px 4px' }}>کد</th>
              <th style={{ textAlign: 'right', padding: '6px 4px' }}>عنوان حساب</th>
              <th style={{ textAlign: 'left', padding: '6px 4px' }}>گردش بدهکار ({label})</th>
              <th style={{ textAlign: 'left', padding: '6px 4px' }}>گردش بستانکار ({label})</th>
              <th style={{ textAlign: 'left', padding: '6px 4px' }}>مانده</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ padding: '14px 4px', color: '#777' }}>
                  سندی قطعی برای این بازه ثبت نشده است.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.accountRef} style={{ borderBottom: '1px solid #e2e2e2' }}>
                  <td
                    className="ltr sk-num"
                    style={{ padding: '6px 4px', fontFamily: 'var(--sk-font-mono)' }}
                  >
                    {row.accountRef}
                  </td>
                  <td style={{ padding: '6px 4px' }}>{row.accountName}</td>
                  <td className="ltr sk-num" style={{ padding: '6px 4px', textAlign: 'left' }}>
                    {BigInt(row.debitMovementRial) > 0n
                      ? formatRialFa(row.debitMovementRial, unit)
                      : '—'}
                  </td>
                  <td className="ltr sk-num" style={{ padding: '6px 4px', textAlign: 'left' }}>
                    {BigInt(row.creditMovementRial) > 0n
                      ? formatRialFa(row.creditMovementRial, unit)
                      : '—'}
                  </td>
                  <td className="ltr sk-num" style={{ padding: '6px 4px', textAlign: 'left' }}>
                    {balanceLabel(row, unit)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {rows.length > 0 ? (
            <tfoot>
              <tr style={{ borderTop: '2px solid #333', fontWeight: 700 }}>
                <td style={{ padding: '6px 4px' }} colSpan={2}>
                  مجموع
                </td>
                <td className="ltr sk-num" style={{ padding: '6px 4px', textAlign: 'left' }}>
                  {formatRialFa(report.data.totals.debitMovementRial, unit)}
                </td>
                <td className="ltr sk-num" style={{ padding: '6px 4px', textAlign: 'left' }}>
                  {formatRialFa(report.data.totals.creditMovementRial, unit)}
                </td>
                <td className="ltr sk-num" style={{ padding: '6px 4px', textAlign: 'left' }}>
                  {balanceLabel(report.data.totals, unit)}
                </td>
              </tr>
            </tfoot>
          ) : null}
        </table>

        <p style={{ fontSize: 11, color: '#666', lineHeight: 1.9, margin: 0 }}>
          {report.data.balanced ? 'تراز متوازن است' : 'تراز نامتوازن است'} · ارقام به {label} · فقط
          اسناد قطعی.
        </p>
      </div>
    </PrintPageShell>
  );
}
